import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import API from "@/lib/api";
import OrgIcon from "@/components/OrgIcon";
import { IQA_FALLBACK as FALLBACK } from "@/data/leaderboard";

const isOurs = (cat) => cat && (cat === "ours" || cat.startsWith("ours") || cat === "fine-tuned");
const isBase = (cat) => cat && (cat === "base" || cat === "baseline");

// Always authoritative — not expected from the API
const PER_SUBJECT = {
  Maths:     { n: 34, base: 26.5, ours: 50.0 },
  Biology:   { n: 37, base: 32.4, ours: 45.9 },
  Physics:   { n: 52, base: 38.5, ours: 50.0 },
  Chemistry: { n: 39, base: 51.3, ours: 53.9 },
};

function BarRow({ label, width, value, isOurs: ours }) {
  return (
    <div className={`s-bar-row${ours ? " ours" : ""}`}>
      <span className="s-bar-label">{label}</span>
      <span className="s-bar-track"><span className="s-bar-fill" style={{ width }} /></span>
      <span className="s-bar-val">{value}</span>
    </div>
  );
}


const SUBJECT_EXAMPLES = {
  Maths: { subj: "Mathematics · geometric construction", body: "A figure with labelled points, angles and lines, where the relationships in the drawing are the problem.", chain: "Read the construction precisely and reason from it. The base model often can't see these relationships at all." },
  Biology: { subj: "Biology · cell diagram", body: "Diagrams of cell organelles or biological structures where labels and spatial relationships carry the answer.", chain: "Correctly identify structures and their functions from a schematic diagram." },
  Physics: { subj: "Physics · circuit schematic", body: "A circuit drawn with standard symbols — resistors, a battery, a junction — where the answer depends on topology.", chain: "Recognise each symbol, read the topology, then apply the right law." },
  Chemistry: { subj: "Chemistry · molecular structure", body: "A structure where the bonds, rings and groups define the molecule; option images may be near-identical isomers.", chain: "Parse the structure exactly and tell near-identical molecules apart. A small misread changes the compound." },
};

export default function NalandaImageVLDetailPage() {
  const [rows, setRows] = useState(FALLBACK);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/hf/imageqa-leaderboard`).then(r => { if (r.data?.rows?.length) setRows(r.data.rows); }).catch(() => {});
  }, []);

  const baseRow = rows.find(r => isBase(r.category));
  const vsBase  = "12.3";
  const topAcc  = Math.max(...rows.map(r => r.accuracy));
  const active  = rows[activeIdx] ?? rows[0];

  return (
    <div className="bg-[#0A0A0A]" style={{ paddingTop: "96px" }}>

      {/* Breadcrumb */}
      <div className="s-breadbar">
        <div className="s-wrap s-breadbar-inner">
          <Link to="/">Home</Link>
          <span style={{ opacity: .4 }}>/</span>
          <Link to="/benchmarks">Benchmarks</Link>
          <span style={{ opacity: .4 }}>/</span>
          <span>Nalanda Image VL</span>
        </div>
      </div>

      {/* Page header */}
      <div className="s-detail-header">
        <div className="s-wrap">
          <p className="s-eyebrow">STEM Science QA · Accuracy</p>
          <h1>Teaching a vision model to read <span className="hl">science diagrams.</span></h1>
          <p className="sub">We built a multimodal STEM dataset paired with diagrams and chain-of-thought answers, then fine-tuned LLaMA-3.2-Vision-11B — training the vision layers alongside the language layers.</p>
        </div>
      </div>

      {/* Stat strip */}
      <div className="s-stat-strip">
        <div className="s-stat-item"><div className="s-stat-num">+{vsBase}<span className="s-stat-unit">pts</span></div><div className="s-stat-lbl">Overall accuracy on the held-out set, 37.7% → 50.0%, after fine-tuning.</div></div>
        <div className="s-stat-item"><div className="s-stat-num">+23.5<span className="s-stat-unit">pts</span></div><div className="s-stat-lbl">Mathematics — accuracy nearly doubled, from 26.5% to 50.0%.</div></div>
        <div className="s-stat-item"><div className="s-stat-num">22,679</div><div className="s-stat-lbl">Multimodal science QA pairs, each with a diagram and chain-of-thought answer.</div></div>
        <div className="s-stat-item"><div className="s-stat-num">162</div><div className="s-stat-lbl">Held-out evaluation questions across 4 STEM subjects.</div></div>
      </div>

      {/* ── Split panel: model leaderboard ── */}
      <div className="s-split-panel">

        {/* LEFT: ranked model list */}
        <div className="s-sp-left">
          <div className="s-sp-head">
            <span className="s-sp-head-label">Leaderboard · Accuracy</span>
            <span className="s-sp-head-label">{rows.length} models</span>
          </div>
          {rows.map((row, i) => (
            <div
              key={row.model}
              className={`s-sp-row${i === activeIdx ? " active" : ""}${isOurs(row.category) ? " ours" : ""}`}
              onClick={() => setActiveIdx(i)}
            >
              <span className="s-sp-rank">{i + 1}</span>
              <div className="s-sp-row-main">
                <div className="s-sp-row-top">
                  <OrgIcon org={row.org} />
                  <span className="s-sp-model">{row.model}</span>
                  {isOurs(row.category) && <span className="s-sp-badge">ours · fine-tuned</span>}
                  {isBase(row.category) && <span className="s-sp-badge" style={{ color: "var(--muted-2)", background: "rgba(255,255,255,.06)" }}>base</span>}
                </div>
                <div className="s-sp-bar-track">
                  <div className="s-sp-bar-fill" style={{ width: `${((row.accuracy / topAcc) * 100).toFixed(1)}%` }} />
                </div>
              </div>
              <div className="s-sp-score-col">
                <div className="s-sp-score">{row.accuracy}%</div>
                <div className="s-sp-delta">{i === 0 ? "—" : `−${(topAcc - row.accuracy).toFixed(1)}`}</div>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: model detail */}
        <div className="s-sp-right">
          <div className="s-sp-detail-head">
            <OrgIcon org={active?.org ?? "Nalandadata"} />
            <span className="s-sp-detail-name">{active?.model}</span>
            <span style={{ color: "var(--muted-2)", margin: "0 4px" }}>|</span>
            <span className="s-sp-detail-score">{active?.accuracy}% acc</span>
          </div>
          <div style={{ fontSize: "12.5px", color: "var(--muted-2)", marginBottom: "18px" }}>
            {active?.n_samples ?? 162} MCQs · {active?.method}
            {active?.vs_base != null && <> · <span style={{ color: "#7fc794" }}>+{active.vs_base}pp vs base</span></>}
          </div>

          <div className="s-sp-tabs">
            <button className="s-sp-tab active">Task</button>
          </div>
          {(() => {
            const subjects = ["Maths", "Biology", "Physics", "Chemistry"];
            const ex = SUBJECT_EXAMPLES[subjects[activeIdx % subjects.length]] ?? SUBJECT_EXAMPLES.Maths;
            return (
              <div className="s-sp-task">
                <div className="s-sp-task-label">Sample question type · {ex.subj}</div>
                <div className="s-sp-task-body">{ex.body}</div>
                <div style={{ fontSize: "12.5px", color: "var(--muted-2)", marginTop: "10px", lineHeight: 1.6 }}>
                  <b style={{ color: "var(--muted)" }}>What it takes:</b> {ex.chain}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Reproducibility ── */}
      <section className="s-band">
        <div className="s-wrap">
          <p className="s-eyebrow">Reproducibility</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, letterSpacing: "-.025em", marginBottom: "12px" }}>Everything here is public.</h2>
          <p style={{ fontSize: "14.5px", color: "var(--muted)", marginBottom: "20px" }}>The dataset sample and held-out evaluation questions are public — the same questions every model on this leaderboard was scored on.</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <a className="s-btn ghost" href="https://huggingface.co/Nalandadata/nalanda-image-vl" target="_blank" rel="noopener noreferrer">Open the model on Hugging Face →</a>
            <a className="s-btn primary" href="https://huggingface.co/datasets/Nalandadata/nalanda-image-qa-sample" target="_blank" rel="noopener noreferrer">Download a data sample →</a>
          </div>
        </div>
      </section>

      {/* ── Full leaderboard ── */}
      <section className="s-band alt" id="results">
        <div className="s-wrap">
          <p className="s-eyebrow">Results</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, letterSpacing: "-.025em", marginBottom: "20px" }}>Full leaderboard</h2>
          <div className="s-key-stat">
            <span className="kd">+{vsBase}pp</span>
            <span className="kl"><b>from QLoRA fine-tuning of LLaMA-3.2-Vision-11B</b>{37.7}% → {50.0}% over the zero-shot base, from a curated 22,679-pair dataset — frontier models still lead on absolute accuracy.</span>
          </div>
          <div className="s-methbox">
            <h4>Methodology</h4>
            <p>Expert ground-truth answers for every held-out question. Each model scored with an identical exact-match scorer over its full response. Decoding is deterministic (greedy / temperature 0). <a href="https://huggingface.co/datasets/Nalandadata/nalanda-image-qa" target="_blank" rel="noopener noreferrer">Full methodology &amp; scoring on Hugging Face →</a></p>
          </div>
          <div className="s-lb-meta">
            <span className="lm">Nalanda Image VL · {162} held-out MCQs · {rows.length} models</span>
            <span className="ll">
              <a href="https://huggingface.co/datasets/Nalandadata/nalanda-image-qa" target="_blank" rel="noopener noreferrer">↗ HF dataset</a>
              <a href="https://huggingface.co/Nalandadata/nalanda-image-vl" target="_blank" rel="noopener noreferrer">↗ Model</a>
              <a href="https://huggingface.co/datasets/Nalandadata/nalanda-image-qa/tree/main/scripts" target="_blank" rel="noopener noreferrer">↗ Eval script</a>
            </span>
          </div>
          <div className="s-tablecard">
            <div className="s-tbl-scroll">
              <table style={{ minWidth: "420px" }}>
                <caption>Nalanda Image VL — {162} held-out STEM MCQs (accuracy, higher is better)</caption>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Method</th>
                    <th style={{ textAlign: "right" }}>Accuracy</th>
                    <th style={{ textAlign: "right" }}>vs base</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.model} className={isOurs(row.category) ? "best" : ""}>
                      <th>
                        {row.model}
                        {isOurs(row.category) && <span className="badge">ours</span>}
                        {isBase(row.category) && <span style={{ color: "var(--accent)", fontFamily: "var(--mono)", fontSize: "11px", marginLeft: "8px" }}>baseline</span>}
                      </th>
                      <td>{row.method}</td>
                      <td style={{ textAlign: "right" }}>{row.accuracy}%</td>
                      <td style={{ textAlign: "right" }}>{row.vs_base != null ? <span className="s-dpos">+{row.vs_base}</span> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-subject table */}
          {Object.keys(PER_SUBJECT).length > 0 && (
            <>
              <h4 className="s-cb-title" style={{ marginTop: "30px" }}>Per-subject accuracy — fine-tuned vs base</h4>
              <div style={{ overflowX: "auto" }}>
                <table className="s-ptype-tbl">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th style={{ textAlign: "right" }}>N</th>
                      <th style={{ textAlign: "right" }}>Base</th>
                      <th style={{ textAlign: "right" }}>Nalanda Image VL</th>
                      <th style={{ textAlign: "right" }}>Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(PER_SUBJECT).sort(([, a], [, b]) => (b.ours - b.base) - (a.ours - a.base)).map(([subj, d]) => (
                      <tr key={subj} className={subj === "Maths" ? "best" : ""}>
                        <th>{subj}</th>
                        <td style={{ textAlign: "right" }}>{d.n}</td>
                        <td style={{ textAlign: "right" }}>{d.base}</td>
                        <td style={{ textAlign: "right" }}>{d.ours}</td>
                        <td style={{ textAlign: "right" }} className="s-dpos">+{(d.ours - d.base).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="s-cb-note">Per-subject accuracy (%) on the {162}-question held-out set. The largest gains land where the base model was weakest — Maths nearly doubled.</p>
            </>
          )}
        </div>
      </section>

      {/* ── 4 steps ── */}
      <section className="s-band" id="method">
        <div className="s-wrap">
          <p className="s-eyebrow">The approach</p>
          <h2 style={{ fontSize: "clamp(22px,3vw,32px)", fontWeight: 800, letterSpacing: "-.025em", marginBottom: "24px" }}>What we did, in four steps.</h2>
          <div className="s-dt-steps">
            <div className="s-dt-step"><div className="num">1</div><div><h5>Curate</h5><p>Filtered from a pool of 180,505 questions spanning physics, mathematics, chemistry and biology — circuits, ray diagrams, geometric constructions, molecular structures and cell diagrams.</p></div></div>
            <div className="s-dt-step"><div className="num">2</div><div><h5>Reason-annotate</h5><p>Each answer carries a full <b>chain-of-thought explanation</b> that identifies the answer, names the underlying principle, and walks from the visual to the result. The model learns the path, not just the label.</p></div></div>
            <div className="s-dt-step"><div className="num">3</div><div><h5>Fine-tune</h5><p>QLoRA fine-tuning of <b>LLaMA-3.2-Vision-11B</b> in 4-bit (rank 32, alpha 64) on a single A100. The key choice: we train the <b>vision layers</b> as well as the language layers — science diagrams sit too far from natural images to freeze the encoder.</p></div></div>
            <div className="s-dt-step"><div className="num">4</div><div><h5>Evaluate</h5><p>Held-out questions the model never saw, each scored with its diagram, against the same model run zero-shot as the baseline. Broken down by subject, so it is clear where the gains land.</p></div></div>
          </div>
          <p style={{ fontSize: "12.5px", color: "var(--muted-2)", marginTop: "14px", lineHeight: 1.6 }}>
            To run your model on the public test set, use the <a href="https://huggingface.co/datasets/Nalandadata/nalanda-image-qa/tree/main/scripts" target="_blank" rel="noopener noreferrer" style={{ color: "var(--muted)" }}>evaluation script on Hugging Face</a> — no form required.
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="s-band alt s-cs-cta">
        <div className="s-wrap">
          <p className="s-eyebrow" style={{ justifyContent: "center" }}>Try it</p>
          <h2 style={{ fontSize: "clamp(25px,3.6vw,40px)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.08, margin: 0, textAlign: "center" }}>Want a model that reads your diagrams?</h2>
          <div className="s-cta-row">
            <a className="s-btn primary" href="https://huggingface.co/datasets/Nalandadata/nalanda-image-qa-sample" target="_blank" rel="noopener noreferrer">Download a sample</a>
            <Link className="s-btn ghost" to="/contact">Talk to a researcher</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
